import api from './api';

/**
 * Project Service
 * Handles all project-related API operations
 */
const projectService = {
  /**
   * Get all projects with optional filtering
   * @param {Object} options - Query options
   * @param {string} options.status - Filter by status
   * @param {string} options.search - Search term
   * @param {number} options.page - Page number
   * @param {number} options.limit - Results per page
   * @returns {Promise<Object>} Projects with pagination data
   */
  getProjects: async (options = {}) => {
    try {
      const { status, search, page = 1, limit = 10, projectIds, selectedLetter, type = 'project', parentId } = options;

      // Build query parameters
      const params = new URLSearchParams();
      if (status) params.append('status', status);
      if (search) params.append('search', search);
      if (page) params.append('page', page);
      if (limit) params.append('limit', limit);
      // Always send type parameter, default to 'project'
      params.append('type', type);
      // Send parentId if provided
      if (parentId) params.append('parentId', parentId);

      // Handle letter filtering - convert to lowercase for API compatibility
      if (selectedLetter && selectedLetter !== 'All') {
        params.append('startsWith', selectedLetter.toLowerCase());
        console.log(`Filtering by letter: ${selectedLetter}`);
      }

      // If we have selected project IDs, add them as a parameter
      if (projectIds && Array.isArray(projectIds) && projectIds.length > 0) {
        // Convert array to comma-separated string
        params.append('ids', projectIds.join(','));
      }

      const queryString = params.toString();
      const url = `/projects${queryString ? `?${queryString}` : ''}`;
      const response = await api.get(url);
      // If we have project IDs, filter client-side regardless of backend support
      // This ensures we get consistent behavior
      if (projectIds && Array.isArray(projectIds) && projectIds.length > 0) {
        console.log('Filtering projects client-side by IDs:', projectIds);
        const data = response.data;
        const filteredData = {
          ...data,
          data: data.data.filter(project => projectIds.includes(project._id))
        };
        return filteredData;
      }

      return response.data;
    } catch (error) {
      console.error('Error fetching projects:', error);
      throw error.message || 'Failed to fetch projects';
    }
  },

  /**
   * Get a single project by ID
   * @param {string} projectId - Project ID
   * @param {Object} options - Additional options
   * @param {boolean} options.includeDocuments - Whether to include documents
   * @param {boolean} options.includeFeatures - Whether to include features
   * @returns {Promise<Object>} Project data
   */
  getProjectById: async (projectId, options = {}) => {
    try {
      const { includeDocuments, includeFeatures } = options;
      
      // Build query parameters
      const params = new URLSearchParams();
      if (includeDocuments) params.append('includeDocuments', true);
      if (includeFeatures) params.append('includeFeatures', true);
      
      const queryString = params.toString();
      const url = `/projects/${projectId}${queryString ? `?${queryString}` : ''}`;
      
      const response = await api.get(url);
      return response.data.data;
    } catch (error) {
      throw error.message || 'Failed to fetch project';
    }
  },

  /**
   * Create a new project
   * @param {Object} projectData - Project data
   * @returns {Promise<Object>} Created project
   */
  createProject: async (projectData) => {
    try {
      const response = await api.post('/projects', projectData);
      return response.data.data;
    } catch (error) {
      throw error.message || 'Failed to create project';
    }
  },

  /**
   * Update a project
   * @param {string} projectId - Project ID
   * @param {Object} projectData - Updated project data
   * @returns {Promise<Object>} Updated project
   */
  updateProject: async (projectId, projectData) => {
    try {
      const response = await api.put(`/projects/${projectId}`, projectData);
      return response.data.data;
    } catch (error) {
      throw error.message || 'Failed to update project';
    }
  },
  
  /**
   * Update last visited project
   * @param {string} projectId - Project ID
   * @returns {Promise<Object|null>} Updated project or null if failed
   */
  updateLastVisited: async (projectId) => {
    try {
      const response = await api.post(`/projects/${projectId}/last-visited`);
      return response.data.data;
    } catch (error) {
      // Don't throw error as this is not critical functionality
      return null;
    }
  },

  /**
   * Delete a project
   * @param {string} projectId - Project ID
   * @returns {Promise<boolean>} Success status
   */
  deleteProject: async (projectId) => {
    try {
      await api.delete(`/projects/${projectId}`);
      return true;
    } catch (error) {
      throw error.message || 'Failed to delete project';
    }
  },
  
  /**
   * Get project statistics
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Project statistics
   */
  getProjectStats: async (projectId) => {
    try {
      const response = await api.get(`/projects/${projectId}/stats`);
      return response.data.data;
    } catch (error) {
      throw error.message || 'Failed to fetch project statistics';
    }
  },
  
  /**
   * Archive a project
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Updated project
   */
  archiveProject: async (projectId) => {
    try {
      const response = await api.patch(`/projects/${projectId}/archive`);
      return response.data.data;
    } catch (error) {
      throw error.message || 'Failed to archive project';
    }
  },
  
  /**
   * Clone a project
   * @param {string} projectId - Project ID to clone
   * @param {Object} options - Clone options
   * @returns {Promise<Object>} Cloned project
   */
  cloneProject: async (projectId, options = {}) => {
    try {
      const response = await api.post(`/projects/${projectId}/clone`, options);
      return response.data.data;
    } catch (error) {
      console.error('Clone project service error:', error);
      
      // Extract the most specific error message available
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Failed to clone project');
      }
    }
  },

  /**
   * Get project members
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Project members list
   */
  getProjectMembers: async (projectId) => {
    try {
      const response = await api.get(`/projects/${projectId}/members`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Add a member to project
   * @param {string} projectId - Project ID
   * @param {Object} memberData - Member data (email and role)
   * @returns {Promise<Object>} Updated project members
   */
  addProjectMember: async (projectId, memberData) => {
    try {
      const response = await api.post(`/projects/${projectId}/members`, memberData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Remove a member from project
   * @param {string} projectId - Project ID
   * @param {string} userId - User ID to remove
   * @returns {Promise<Object>} Response message
   */
  removeProjectMember: async (projectId, userId) => {
    try {
      const response = await api.delete(`/projects/${projectId}/members/${userId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Update member role in project
   * @param {string} projectId - Project ID
   * @param {string} userId - User ID
   * @param {Object} roleData - New role data
   * @returns {Promise<Object>} Updated member info
   */
  updateMemberRole: async (projectId, userId, roleData) => {
    try {
      const response = await api.patch(`/projects/${projectId}/members/${userId}`, roleData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Toggle task completion status
   * @param {string} projectId - Project ID
   * @param {string} taskId - Task ID to toggle
   * @returns {Promise<Object>} Updated project with completion status
   */
  toggleTaskStatus: async (projectId, taskId) => {
    try {
      const response = await api.patch(`/projects/${projectId}/tasks/${taskId}/toggle`);
      return {
        project: response.data.data,
        isCompleted: response.data.isCompleted
      };
    } catch (error) {
      throw error.message || 'Failed to toggle task status';
    }
  },

  /**
   * Get project with all related details populated for edit form
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Detailed project data with all related entities
   */
  getProjectWithDetails: async (projectId) => {
    console.log(`=== DEBUG === About to call API: /projects/${projectId}/details`);
    
    // Log the API configuration (baseURL)
    try {
      console.log('=== DEBUG === API config:', api.defaults);
      console.log('=== DEBUG === Using URL:', `${api.defaults.baseURL}/projects/${projectId}/details`);
    } catch (configError) {
      console.error('=== DEBUG === Unable to log API config:', configError);
    }
    
    try {
      console.log(`=== DEBUG === Calling API: /projects/${projectId}/details`);
      const response = await api.get(`/projects/${projectId}/details`);
      console.log('=== DEBUG === Project details API response:', response.data);
      return response.data.data;
    } catch (error) {
      console.error('=== DEBUG === Error fetching project details:', error);
      console.error('Error details:', error.response ? error.response.data : 'No response data');
      
      // Try a fallback to the regular endpoint to see if that works
      console.log('=== DEBUG === Trying fallback to regular endpoint');
      try {
        const fallbackResponse = await api.get(`/projects/${projectId}`);
        console.log('=== DEBUG === Fallback API response:', fallbackResponse.data);
        return { project: fallbackResponse.data.data, fallback: true };
      } catch (fallbackError) {
        console.error('=== DEBUG === Fallback also failed:', fallbackError);
      }
      
      throw error.message || 'Failed to fetch project details';
    }
  },

  /**
   * Get project menu visibility settings
   * @returns {Promise<Object>} Menu settings
   */
  getMenuSettings: async () => {
    try {
      const response = await api.get('/projects/menu-settings');
      return response.data.data;
    } catch (error) {
      throw error.message || 'Failed to get menu settings';
    }
  },

  /**
   * Update project menu visibility settings
   * @param {Object} settings - Menu settings
   * @returns {Promise<Object>} Updated settings
   */
  updateMenuSettings: async (settings) => {
    try {
      const response = await api.post('/projects/menu-settings', settings);
      return response.data.data;
    } catch (error) {
      throw error.message || 'Failed to update menu settings';
    }
  },

  /**
   * Get foundation dashboard data (project, sections, tasks with conversations)
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Foundation dashboard data
   */
  getFoundationDashboard: async (projectId) => {
    try {
      const response = await api.get(`/projects/${projectId}/foundation-dashboard`);
      return response.data.data;
    } catch (error) {
      throw error.message || 'Failed to fetch foundation dashboard';
    }
  },

  updateMemberType: async (projectId, userId, memberType) => {
    try {
      const response = await api.patch(`/projects/${projectId}/members/${userId}/type`, {
        member_type: memberType
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  updateCanAssignValidators: async (projectId, userId, canAssign) => {
    try {
      const response = await api.patch(`/projects/${projectId}/members/${userId}/can-assign-validators`, {
        can_assign_validators: canAssign
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }
};

export default projectService;